import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * useInView - Custom hook for detecting when an element enters the viewport
 * 
 * @param {Object} options - IntersectionObserver options
 * @param {number} options.threshold - Visibility threshold (0-1). Default: 0.1 (10% visible)
 * @param {string} options.rootMargin - Margin around root. Default: '50px' (trigger 50px before visible)
 * @param {boolean} options.triggerOnce - Only trigger animation once. Default: true
 * @param {boolean} options.skip - Skip observation (for above-the-fold content). Default: false
 * 
 * @returns {Object} { ref, isInView, hasAnimated }
 * 
 * @example
 * const { ref, isInView } = useInView({ threshold: 0.2 });
 * return <div ref={ref} className={isInView ? 'visible' : 'hidden'}>Content</div>
 */
const useInView = (options = {}) => {
    const {
        threshold = 0.1,
        rootMargin = '50px',
        triggerOnce = true,
        skip = false,
    } = options;

    const ref = useRef(null);
    const [isInView, setIsInView] = useState(skip); // If skip, consider it always in view
    const [hasAnimated, setHasAnimated] = useState(skip);

    const handleIntersection = useCallback((entries) => {
        const [entry] = entries;

        if (entry.isIntersecting) {
            setIsInView(true);
            setHasAnimated(true);
        } else if (!triggerOnce) {
            setIsInView(false);
        }
    }, [triggerOnce]);

    useEffect(() => {
        if (skip) return;

        const element = ref.current;
        if (!element) return;

        // Check if IntersectionObserver is supported
        if (!('IntersectionObserver' in window)) {
            // Fallback: just show everything
            setIsInView(true);
            setHasAnimated(true);
            return;
        }

        const observer = new IntersectionObserver(handleIntersection, {
            threshold,
            rootMargin,
        });

        observer.observe(element);

        return () => {
            if (element) {
                observer.unobserve(element);
            }
            observer.disconnect();
        };
    }, [threshold, rootMargin, skip, handleIntersection]);

    return { ref, isInView, hasAnimated };
};

export default useInView;
